/**
 * ADR-0242 — typed client for the operator-logo endpoints.
 *
 *   GET /v1/agent-logos            → { logos: [{ agent_id, v }] }
 *   GET /v1/agent-logos/:agent_id  → image bytes (PNG / ICO / WebP / JPEG)
 *
 * Logos are only ever loaded from Sill's own API (never hot-linked from an
 * operator's site or a third-party favicon service: either would tell
 * them which merchant is looking at which bot). Defensive parsing: an
 * absent or malformed manifest is an empty one.
 */

import type { HttpClient } from './http-client.js';

const AGENT_ID_RE = /^agent_[a-z0-9_]{1,58}$/;
const VERSION_RE = /^[0-9a-f]{16}$/;
const IMAGE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/x-icon', 'image/webp', 'image/jpeg']);

/** agent_id → logo version. */
export type AgentLogoManifest = ReadonlyMap<string, string>;

export function parseAgentLogoManifest(raw: unknown): AgentLogoManifest {
  const out = new Map<string, string>();
  const logos = typeof raw === 'object' && raw !== null ? (raw as { logos?: unknown }).logos : undefined;
  if (!Array.isArray(logos)) return out;
  for (const l of logos.slice(0, 2000)) {
    if (typeof l !== 'object' || l === null) continue;
    const { agent_id, v } = l as { agent_id?: unknown; v?: unknown };
    if (typeof agent_id === 'string' && AGENT_ID_RE.test(agent_id) && typeof v === 'string' && VERSION_RE.test(v)) {
      out.set(agent_id, v);
    }
  }
  return out;
}

export async function fetchAgentLogoManifest(http: HttpClient): Promise<AgentLogoManifest> {
  return parseAgentLogoManifest(await http.request<unknown>({ method: 'GET', path: '/v1/agent-logos' }));
}

/** The logo bytes, or null when the response is not an allowed image type. */
export async function fetchAgentLogoBlob(http: HttpClient, agent_id: string, v: string): Promise<Blob | null> {
  if (!AGENT_ID_RE.test(agent_id) || !VERSION_RE.test(v)) return null;
  const blob = await http.requestBlob({ method: 'GET', path: `/v1/agent-logos/${agent_id}?v=${v}` });
  return IMAGE_TYPES.has(blob.type) ? blob : null;
}
